import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaEye,
  FaPlay,
  FaSpinner,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaUsers,
  FaCheckCircle,
  FaTimes,
  FaSearch,
  FaFilter,
  FaYoutube,
  FaExternalLinkAlt,
  FaMicrophone,
  FaSync
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RunningPaidConference = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reporterToReject, setReporterToReject] = useState(null);

  // Search/filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  // Set startDate and endDate to today when todayOnly is checked
  useEffect(() => {
    if (todayOnly) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [todayOnly]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, startDate, endDate, todayOnly]);

  // Date helper function
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };

  // Function to filter conferences based on search text and date filters
  const getFilteredConferences = () => {
    // Safety check to ensure conferences is an array
    if (!Array.isArray(conferences)) {
      console.log("Conferences is not an array:", conferences);
      return [];
    }

    const q = searchText.trim().toLowerCase();
    const filtered = conferences.filter((conference) => {
      // Search filter
      if (q) {
        // Check conferenceId
        if (conference.conferenceId && conference.conferenceId.toString().trim().toLowerCase().includes(q)) return true;
        // Check topic
        if (conference.topic && conference.topic.trim().toLowerCase().includes(q)) return true;
        // Check submitter name
        if (conference.submittedBy?.name && conference.submittedBy.name.trim().toLowerCase().includes(q)) return true;
        // Check all relevant fields in accepted reporters
        if (
          conference.acceptedReporters &&
          conference.acceptedReporters.some((reporter) => {
            const iinsafId = reporter.iinsafId ? reporter.iinsafId.toString().trim().toLowerCase() : "";
            const reporterName = reporter.reporterName ? reporter.reporterName.trim().toLowerCase() : "";
            const reporterEmail = reporter.reporterEmail ? reporter.reporterEmail.trim().toLowerCase() : "";
            return (
              iinsafId.includes(q) ||
              reporterName.includes(q) ||
              reporterEmail.includes(q)
            );
          })
        ) {
          return true;
        }
        return false;
      }

      // Date filter
      const conferenceDate = toDate(conference.conferenceDate);
      const filterStart = toDate(startDate);
      const filterEnd = toDate(endDate, true);

      // Skip conferences with invalid dates if filters are active
      if ((todayOnly || filterStart || filterEnd) && !conferenceDate) {
        return false;
      }

      // Apply date filters
      if (filterStart && conferenceDate < filterStart) return false;
      if (filterEnd && conferenceDate > filterEnd) return false;

      return true;
    });
    console.log("Search term:", searchText, "Filtered conferences:", filtered);
    return filtered;
  };

  // Pagination logic
  const filteredConferences = getFilteredConferences();
  const totalPages = Math.ceil((filteredConferences?.length || 0) / rowsPerPage);
  const paginatedConferences = (filteredConferences || []).slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchRunningConferences = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/with-proofs`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched Running Conferences Data:", response);
        console.log("Response data structure:", response.data);

        // Handle the correct response structure: { success: true, data: { conferences: [...], totalPages: ..., currentPage: ..., total: ... } }
        let conferencesData = [];
        if (response.data && response.data.data && response.data.data.conferences) {
          conferencesData = Array.isArray(response.data.data.conferences) ? response.data.data.conferences : [];
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          conferencesData = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          conferencesData = response.data;
        }

        console.log("Processed conferences data:", conferencesData);
        setConferences(conferencesData);
      } catch (err) {
        setError("Failed to fetch running conferences");
      } finally {
        setLoading(false);
      }
    };
    fetchRunningConferences();
  }, []);

  // View conference details with reporters
  const handleViewDetails = async (conference) => {
    try {
      const token = localStorage.getItem("token");
      console.log("View details token:", token);
      console.log("Conference ID:", conference.conferenceId);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conference.conferenceId}/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Conference details response:", response.data);
      if (response.data.success) {
        setSelectedConference(response.data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Error fetching conference details:", error);
      console.error("Error response:", error.response?.data);
    }
  };

  const handleViewProof = (conference) => {
    setSelectedProof(conference);
    setShowProofModal(true);
  };

  const handleApproveProof = async (conferenceId, reporterId) => {
    // 🔑 CRITICAL FIX: Prevent multiple clicks and duplicate approvals
    if (actionLoading) {
      console.log("Already processing approval, ignoring duplicate click");
      return;
    }

    try {
      setActionLoading(true);
      console.log("Approving proof for conference:", conferenceId, "reporter:", reporterId);
      console.log("Reporter ID type:", typeof reporterId);
      console.log("Reporter ID value:", reporterId);

      // 🔑 CRITICAL FIX: Generate unique idempotency key to prevent duplicate approvals
      const idempotencyKey = `approve_${conferenceId}_${reporterId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conferenceId}/complete`,
        {
          reporterId,
          idempotencyKey
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            // 🔑 CRITICAL FIX: Add timeout to prevent hanging requests
            timeout: 15000
          },
        }
      );

      if (response.data.success) {
        // Refresh the conferences list
        window.location.reload();
        setShowProofModal(false);
        toast.success("Proof approved successfully! Reporter wallet credited.");
      }
    } catch (error) {
      console.error("Error approving proof:", error);

      // 🔑 CRITICAL FIX: Handle specific error messages from backend
      let errorMessage = "Failed to approve proof";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;

        // Handle specific duplicate approval error
        if (errorMessage.includes("already approved")) {
          errorMessage = "This proof has already been approved. Please refresh the page.";
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timed out. Please try again.";
      }

      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectProof = async (conferenceId, reporterId, rejectReason) => {
    try {
      setActionLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conferenceId}/reject-proof`,
        { reporterId, rejectReason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the conferences list
        window.location.reload();
        setShowProofModal(false);
        setShowRejectModal(false);
        setRejectReason("");
        setReporterToReject(null);
        toast.success("Proof rejected successfully!");
      }
    } catch (error) {
      console.error("Error rejecting proof:", error);
      toast.error("Failed to reject proof");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (reporterId, reporterName) => {
    setReporterToReject({ reporterId, reporterName });
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a reason for rejection");
      return;
    }
    handleRejectProof(selectedProof.conferenceId, reporterToReject.reporterId, rejectReason.trim());
  };



  return (
    <>

      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative w-full sm:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="w-5 h-5 text-gray-500" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search by Conference ID, Topic, or Reporter Name..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          <button
            onClick={() => {
              setSearchText("");
              setStartDate("");
              setEndDate("");
              setTodayOnly(false);
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>

        {/* Date Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="todayOnly"
              checked={todayOnly}
              onChange={(e) => setTodayOnly(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="todayOnly" className="text-sm font-medium text-gray-700">
              Today Only
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={todayOnly}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={todayOnly}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            Paid Conferences with Submitted Proofs: {filteredConferences?.length || 0}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Showing approved, modified, and running conferences that have submitted proofs for review
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/paid-conference/pending")}
          >
            Pending
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-green-500 text-white rounded hover:bg-green-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/paid-conference/approved")}
          >
            Approved
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/paid-conference/rejected")}
          >
            Rejected
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-orange-500 text-white rounded hover:bg-orange-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/paid-conference/modified")}
          >
            Modified
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-purple-500 text-white rounded hover:bg-purple-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/paid-conference/completed")}
          >
            Completed
          </button>
        </div>
      </div>

      {/* Conference Table Design */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : !Array.isArray(conferences) || conferences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaMicrophone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">
            No Paid Conferences with Submitted Proofs
          </h3>
          <p className="text-gray-500">
            There are no approved, modified, or running paid conferences with submitted proofs at the moment.
          </p>
        </div>
      ) : paginatedConferences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaMicrophone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">
            No Results Found
          </h3>
          <p className="text-gray-500">
            {filteredConferences.length === 0
              ? "No conferences match your search criteria."
              : "No conferences on this page."
            }
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
            <thead>
              <tr className="bg-gray-100 text-sm text-gray-700">
                <th className="px-4 py-3 text-left">Conference ID</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Topic</th>
                <th className="px-4 py-3 text-left">Purpose</th>
                <th className="px-4 py-3 text-left">Date & Time</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Payment Info</th>
                <th className="px-4 py-3 text-left">Proof Status</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedConferences.map((conference) => (
                <tr key={conference._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm">{conference.conferenceId}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${conference.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : conference.status === "modified"
                          ? "bg-purple-100 text-purple-800"
                          : conference.status === "running"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                      }`}>
                      {conference.status === "approved" && <FaCheckCircle className="w-3 h-3 mr-1" />}
                      {conference.status === "modified" && <FaSync className="w-3 h-3 mr-1" />}
                      {conference.status === "running" && <FaPlay className="w-3 h-3 mr-1" />}
                      {conference.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{conference.topic}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                    {conference.purpose}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaCalendarAlt className="mr-1 text-blue-500" />
                      {conference.conferenceDate}
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <FaClock className="mr-1" />
                      {conference.conferenceTime} {conference.timePeriod}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaMapMarkerAlt className="mr-1 text-green-500" />
                      {conference.city}, {conference.state}
                    </div>
                    <div className="text-xs text-gray-500">{conference.place}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-green-600">
                      ₹{conference.commissionDetails?.amountPerReporter || (conference.paymentAmount / conference.numberOfReporters).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {conference.numberOfReporters} reporters
                    </div>
                    <div className="text-xs text-gray-500">
                      Total: ₹{conference.paymentAmount}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaCheckCircle className="mr-1 text-green-500" />
                      {conference.totalProofSubmitted || 0} submitted
                    </div>
                    <div className="text-xs text-gray-500">
                      {conference.totalAccepted - conference.totalProofSubmitted || 0} pending
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(conference)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm flex items-center"
                      >
                        <FaEye className="mr-1" />
                        View Details
                      </button>
                      {(conference.totalProofSubmitted > 0 ||
                        (conference.acceptedReporters && conference.acceptedReporters.some(reporter => reporter.proofSubmitted))) && (
                          <button
                            onClick={() => handleViewProof(conference)}
                            className="px-3 py-1 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-sm flex items-center"
                          >
                            <FaCheckCircle className="mr-1" />
                            Review Proofs
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
          {/* Page Info */}
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredConferences?.length || 0)} of {filteredConferences?.length || 0} conferences
          </div>

          {/* Pagination Navigation */}
          <nav className="inline-flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 rounded border transition-colors ${currentPage === pageNum
                        ? 'border-blue-400 bg-blue-300 text-blue-900 font-bold'
                        : 'border-blue-400 text-blue-800 bg-white hover:bg-blue-100'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Conference Details Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Conference Details - {selectedConference.conferenceId}
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                {/* Conference Information */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Conference Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-600">Topic:</span>
                      <p className="text-gray-900">{selectedConference.topic}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Date & Time:</span>
                      <p className="text-gray-900">{selectedConference.conferenceDate} at {selectedConference.conferenceTime} {selectedConference.timePeriod}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Location:</span>
                      <p className="text-gray-900">{selectedConference.city}, {selectedConference.state}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Venue:</span>
                      <p className="text-gray-900">{selectedConference.place}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-gray-600">Purpose:</span>
                    <p className="text-gray-900">{selectedConference.purpose}</p>
                  </div>
                  <div className="mt-3 p-3 bg-green-50 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-2">Payment Information</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Total Amount:</span>
                        <p className="text-green-600 font-semibold">₹{selectedConference.paymentAmount}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Reporters Needed:</span>
                        <p className="text-gray-900">{selectedConference.numberOfReporters}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Amount per Reporter:</span>
                        <p className="text-green-600 font-semibold">₹{selectedConference.commissionDetails?.amountPerReporter || (selectedConference.paymentAmount / selectedConference.numberOfReporters).toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Commission:</span>
                        <p className="text-gray-900">{selectedConference.commissionDetails?.commissionPercentage || 0}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Accepted Reporters List */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">
                    Accepted Reporters ({selectedConference.acceptedReporters?.length || 0})
                  </h4>

                  {(!selectedConference.acceptedReporters || selectedConference.acceptedReporters.length === 0) ? (
                    <p className="text-gray-500 text-center py-4">No reporters have accepted this conference yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Reporter
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              IINSAF ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Location
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Accepted At
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Proof Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedConference.acceptedReporters.map((reporter, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {reporter.reporterName}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {reporter.reporterEmail}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {reporter.iinsafId}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {reporter.reporterCity}, {reporter.reporterState}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(reporter.acceptedAt).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {reporter.proofSubmitted ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <FaCheckCircle className="mr-1" />
                                    Submitted
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <FaClock className="mr-1" />
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof Review Modal */}
      {showProofModal && selectedProof && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowProofModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Review Submitted Proofs - {selectedProof.conferenceId}
                  </h3>
                  <button
                    onClick={() => setShowProofModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {selectedProof.reportersWithProofs
                    ?.map((reporter, index) => (
                      <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">
                        {/* Reporter Header */}
                        <div className="flex justify-between items-start mb-6">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                              <FaUser className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="text-xl font-semibold text-gray-900">
                                {reporter.reporterName}
                              </h4>
                              <div className="flex items-center space-x-4 mt-1">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  ID: {reporter.iinsafId}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {reporter.reporterEmail}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="bg-green-100 rounded-lg p-3">
                              <p className="text-xs text-green-600 font-medium">Will Receive</p>
                              <p className="text-lg font-bold text-green-700">
                                ₹{selectedProof.commissionDetails?.amountPerReporter || (selectedProof.paymentAmount / selectedProof.numberOfReporters).toFixed(2)}
                              </p>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">
                              Submitted: {reporter.proof?.submittedAt ? new Date(reporter.proof.submittedAt).toLocaleDateString() : "N/A"}
                            </p>
                          </div>
                        </div>

                        {/* Proof Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          {/* Video Information */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center mb-3">
                              <FaYoutube className="w-5 h-5 text-red-600 mr-2" />
                              <h5 className="font-semibold text-gray-800">Video Information</h5>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Video Link</label>
                                {reporter.proof?.videoLink ? (
                                  <a
                                    href={reporter.proof.videoLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-1 text-blue-600 hover:text-blue-800 flex items-center text-sm break-all"
                                  >
                                    {reporter.proof.videoLink}
                                    <FaExternalLinkAlt className="w-3 h-3 ml-1 flex-shrink-0" />
                                  </a>
                                ) : (
                                  <span className="mt-1 text-gray-500 text-sm">No video link provided</span>
                                )}
                              </div>

                              {reporter.proof?.channelName && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Channel Name</label>
                                  <p className="mt-1 text-gray-900 font-medium">{reporter.proof.channelName}</p>
                                </div>
                              )}

                              {reporter.proof?.platform && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Platform</label>
                                  <p className="mt-1 text-gray-900 font-medium">{reporter.proof.platform}</p>
                                </div>
                              )}

                              {reporter.proof?.duration && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Duration</label>
                                  <p className="mt-1 text-gray-900 font-medium">{reporter.proof.duration}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Status Information */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center mb-3">
                              <FaCheckCircle className="w-5 h-5 text-green-600 mr-2" />
                              <h5 className="font-semibold text-gray-800">Status Information</h5>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Proof Status</label>
                                <div className="mt-1">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${reporter.proof?.status === 'approved'
                                      ? 'bg-green-100 text-green-800'
                                      : reporter.proof?.status === 'rejected'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                    {reporter.proof?.status === 'approved' && <FaCheckCircle className="w-3 h-3 mr-1" />}
                                    {reporter.proof?.status === 'rejected' && <FaTimes className="w-3 h-3 mr-1" />}
                                    {(!reporter.proof?.status || reporter.proof?.status === 'pending') && <FaClock className="w-3 h-3 mr-1" />}
                                    {reporter.proof?.status || 'pending'}
                                  </span>
                                </div>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Submission Date</label>
                                <p className="mt-1 text-gray-900 font-medium">
                                  {reporter.proof?.submittedAt ? new Date(reporter.proof.submittedAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) : "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Screenshot Section */}
                        {reporter.proof?.screenshot && (
                          <div className="bg-white rounded-lg p-4 border border-gray-200 mb-6">
                            <div className="flex items-center mb-3">
                              <svg className="w-5 h-5 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                              <h5 className="font-semibold text-gray-800">Proof Screenshot</h5>
                            </div>
                            <div className="relative">
                              <img
                                src={reporter.proof.screenshot.startsWith('http')
                                  ? reporter.proof.screenshot
                                  : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${reporter.proof.screenshot}`
                                }
                                alt="Proof Screenshot"
                                className="w-full h-auto rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-all duration-200 hover:shadow-lg"
                                style={{ maxHeight: '300px', objectFit: 'contain' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                                onClick={() => {
                                  const imageUrl = reporter.proof.screenshot.startsWith('http')
                                    ? reporter.proof.screenshot
                                    : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${reporter.proof.screenshot}`;
                                  window.open(imageUrl, '_blank');
                                }}
                              />
                              <div
                                className="hidden bg-gray-100 rounded-lg border border-gray-200 p-8 text-center"
                                style={{ minHeight: '200px' }}
                              >
                                <div className="text-gray-500">
                                  <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                  </svg>
                                  <p className="text-sm">Screenshot not available</p>
                                  <p className="text-xs text-gray-400 mt-1">Image file may be missing or corrupted</p>
                                </div>
                              </div>
                              <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                                Click to view full size
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => {
                              // Send the _id field instead of the entire object
                              const reporterIdToSend = reporter.reporterId?._id || reporter.reporterId;
                              openRejectModal(reporterIdToSend, reporter.reporterName);
                            }}
                            disabled={actionLoading}
                            className="flex items-center px-6 py-3 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors duration-200"
                          >
                            <FaTimes className="w-4 h-4 mr-2" />
                            {actionLoading ? "Processing..." : "Reject Proof"}
                          </button>
                          <button
                            onClick={() => {
                              console.log("Reporter data:", reporter);
                              console.log("Reporter ID object:", reporter.reporterId);
                              console.log("Reporter ID _id:", reporter.reporterId?._id);
                              console.log("Reporter ID type:", typeof reporter.reporterId);
                              // Send the _id field instead of the entire object
                              const reporterIdToSend = reporter.reporterId?._id || reporter.reporterId;
                              console.log("Sending reporter ID:", reporterIdToSend);
                              handleApproveProof(selectedProof.conferenceId, reporterIdToSend);
                            }}
                            disabled={actionLoading}
                            className="flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors duration-200"
                          >
                            <FaCheckCircle className="w-4 h-4 mr-2" />
                            {actionLoading ? "Processing..." : "Approve Proof"}
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 text-left relative animate-fadeIn border border-red-200">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold"
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason("");
                setReporterToReject(null);
              }}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTimes className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Reject Proof</h2>
              <p className="text-gray-600">
                Rejecting proof for <span className="font-semibold">{reporterToReject?.reporterName}</span>
              </p>
            </div>

            {/* Rejection Reason Input */}
            <div className="mb-6">
              <label htmlFor="rejectReason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a clear reason for rejecting this proof..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={4}
                required
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                  setReporterToReject(null);
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={actionLoading || !rejectReason.trim()}
                className="px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {actionLoading ? (
                  <>
                    <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <FaTimes className="w-4 h-4 mr-2" />
                    Reject Proof
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default RunningPaidConference;
